import type { HashMap } from 'shared/types/commonView';
import type { DocumentationModel } from 'shared/domain/documentation/documentationModel';

export function withPendingRemovalId(
  ids: HashMap<true>,
  linkedDocumentationId: string
): HashMap<true> {
  return {
    ...ids,
    [linkedDocumentationId]: true,
  };
}

export function withoutPendingRemovalId(
  ids: HashMap<true>,
  linkedDocumentationId: string
): HashMap<true> {
  if (!ids[linkedDocumentationId]) {
    return ids;
  }

  const nextIds = { ...ids };
  delete nextIds[linkedDocumentationId];
  return nextIds;
}

/**
 * Drop in-progress binary/local sources from details cache entries.
 * Versions can carry blob:/data: localData that would pin large blobs in Zustand.
 */
export function stripDocumentationModelForCache(
  details: DocumentationModel
): DocumentationModel {
  if (!details.versions?.some((version) => version.localData !== undefined)) {
    return details;
  }

  return {
    ...details,
    versions: details.versions.map((version) => {
      if (version.localData === undefined) {
        return version;
      }

      const { localData: _localData, ...rest } = version;
      return rest;
    }),
  };
}

export function pickHashMapEntries<T>(
  values: HashMap<T>,
  keepKeys: Set<string>
): HashMap<T> {
  if (keepKeys.size === 0) {
    return {};
  }

  const next: HashMap<T> = {};
  keepKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      next[key] = values[key];
    }
  });
  return next;
}

export function setBoundedMetaEntry<TMeta>(
  metas: HashMap<TMeta>,
  key: string,
  meta: TMeta,
  maxEntries: number
): HashMap<TMeta> {
  const nextMetas = { ...metas };
  const hasKey = Object.prototype.hasOwnProperty.call(nextMetas, key);

  if (hasKey) {
    delete nextMetas[key];
  } else {
    const keys = Object.keys(nextMetas);
    if (keys.length >= maxEntries) {
      const oldestKey = keys[0];
      if (oldestKey !== undefined) {
        delete nextMetas[oldestKey];
      }
    }
  }

  nextMetas[key] = meta;
  return nextMetas;
}

export function setBoundedCachePair<TValue, TMeta>(
  values: HashMap<TValue>,
  metas: HashMap<TMeta>,
  key: string,
  value: TValue,
  meta: TMeta,
  maxEntries: number
): { values: HashMap<TValue>; metas: HashMap<TMeta> } {
  const nextValues = { ...values };
  const nextMetas = { ...metas };
  const hasKey = Object.prototype.hasOwnProperty.call(nextValues, key);

  if (hasKey) {
    delete nextValues[key];
    delete nextMetas[key];
  } else {
    const keys = Object.keys(nextValues);
    if (keys.length >= maxEntries) {
      const oldestKey = keys[0];
      if (oldestKey !== undefined) {
        delete nextValues[oldestKey];
        delete nextMetas[oldestKey];
      }
    }
  }

  nextValues[key] = value;
  nextMetas[key] = meta;

  return { values: nextValues, metas: nextMetas };
}
